import React, { useMemo, useState, useCallback } from 'react';
import { MCarouselContainer } from '../MCarouselContainer';
import * as Styled from './MChatImageCarousel.styles';
import { MChatImageCarouselProps, PropertyData, PropertyMediaFromPreview } from './MChatImageCarousel.types';

const SKELETON_COUNT = 10;

const CACHE_DOMAIN = 'https://cache.marriott.com';
const MEDIA_CATEGORY_ORDER = [
  'hotelView',
  'suites',
  'guestRooms',
  'recreationAndFitness',
  'dining',
  'eventsAndMeetings',
  'features',
  'golf',
  'services',
  'spa',
  'villas',
  'activities',
  'weddings',
] as const;

const toAbsoluteUrl = (url: string): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${CACHE_DOMAIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

const getPrimaryImageUrls = (propertyData?: PropertyData): string[] => {
  const edges = propertyData?.media?.primaryImage?.edges ?? [];
  return edges
    .map(edge => {
      const imageUrls = edge?.node?.imageUrls;
      return `${
        imageUrls?.classicHorizontal || imageUrls?.wideHorizontal || imageUrls?.classic || imageUrls?.square
      }?downsize=201px:*`;
    })
    .filter(Boolean);
};

const getGalleryImageUrls = (mediaFromPreview?: PropertyMediaFromPreview): string[] => {
  const photoGallery = mediaFromPreview?.media?.photoGallery;
  if (!photoGallery) return [];

  const galleryUrls: string[] = [];
  MEDIA_CATEGORY_ORDER.forEach(category => {
    const edges = photoGallery?.[category]?.edges ?? [];
    edges.forEach(edge => {
      const imageUrls = edge?.node?.imageUrls;
      const url = `${imageUrls?.classicHorizontal || imageUrls?.wideHorizontal || ''}?downsize=201px:*`;
      if (url) galleryUrls.push(url);
    });
  });
  return galleryUrls;
};

export const MChatImageCarousel: React.FC<MChatImageCarouselProps> = ({
  propertyName,
  propertyData,
  mediaFromPreview,
  maxImages = 10,
  onImageClick,
}) => {
  const [loadedUrls, setLoadedUrls] = useState<Set<string>>(new Set());
  const [failedUrls, setFailedUrls] = useState<Set<string>>(new Set());
  const [hasStartedLoading, setHasStartedLoading] = useState(false);

  const normalizedImages = useMemo(() => {
    // Build image list to match SERP behavior:
    // primary image first, then ordered media-gallery categories, de-duplicated and capped.
    const primaryUrls = getPrimaryImageUrls(propertyData);
    const galleryUrls = getGalleryImageUrls(mediaFromPreview);
    const mergedUniqueUrls = Array.from(new Set([...primaryUrls, ...galleryUrls]));
    return mergedUniqueUrls.slice(0, maxImages).map(toAbsoluteUrl).filter(Boolean);
  }, [propertyData, mediaFromPreview, maxImages]);

  const visibleImages = useMemo(
    () => normalizedImages.filter(url => !failedUrls.has(url)),
    [normalizedImages, failedUrls]
  );

  const handleImageLoad = useCallback((url: string) => {
    setLoadedUrls(prev => new Set(prev).add(url));
    setHasStartedLoading(true);
  }, []);

  const handleImageError = useCallback((url: string) => {
    setFailedUrls(prev => new Set(prev).add(url));
    setHasStartedLoading(true);
  }, []);

  const showSkeletons = normalizedImages.length === 0 || !hasStartedLoading;

  if (visibleImages.length === 0 && hasStartedLoading) return null;

  return (
    <>
      {/* Hidden preload images to trigger loading */}
      {normalizedImages.map((url, index) => (
        <img
          key={`${propertyName}-preload-${index}`}
          src={url}
          alt=""
          style={{ display: 'none', position: 'absolute', width: 0, height: 0 }}
          onLoad={() => handleImageLoad(url)}
          onError={() => handleImageError(url)}
        />
      ))}
      <MCarouselContainer fixedSlideWidth={200} gap={8} bleedLeft={24}>
        {showSkeletons
          ? Array.from({ length: SKELETON_COUNT }).map((_, index) => (
              <Styled.SlideCard key={`${propertyName}-skeleton-${index}`}>
                <Styled.SkeletonSlide className="skeleton-loader" aria-hidden="true" />
              </Styled.SlideCard>
            ))
          : visibleImages.map((url, index) => (
              <Styled.SlideCard key={`${propertyName}-image-${url}`}>
                {onImageClick ? (
                  <Styled.SlideButton
                    type="button"
                    aria-label={`Open photo ${index + 1} for ${propertyName}`}
                    onClick={() => onImageClick(index, url)}
                  >
                    {!loadedUrls.has(url) && <Styled.SkeletonSlide className="skeleton-loader" aria-hidden="true" />}
                    <Styled.SlideImage
                      src={url}
                      alt={propertyName}
                      onLoad={() => handleImageLoad(url)}
                      onError={() => handleImageError(url)}
                      $loaded={loadedUrls.has(url)}
                    />
                  </Styled.SlideButton>
                ) : (
                  <>
                    {!loadedUrls.has(url) && <Styled.SkeletonSlide className="skeleton-loader" aria-hidden="true" />}
                    <Styled.SlideImage
                      src={url}
                      alt={propertyName}
                      onLoad={() => handleImageLoad(url)}
                      onError={() => handleImageError(url)}
                      $loaded={loadedUrls.has(url)}
                    />
                  </>
                )}
              </Styled.SlideCard>
            ))}
      </MCarouselContainer>
    </>
  );
};
